import { useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router';
import { Button, Card } from '@/ui/primitives.js';
import { deleteSession } from '@/lib/repo.js';
import { track } from '@/lib/analytics.js';

/**
 * "This one was measured wrong" — removes the session from history, stats and
 * anything shared, after a confirmation that lives on the page (the native
 * WebView shows no confirm() dialogs).
 */
export function DeleteSession({ sessionId }: { sessionId: string }): ReactNode {
  const navigate = useNavigate();
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const remove = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      await deleteSession(sessionId);
      track('session_deleted', { from: 'report' });
      navigate('/progress', { replace: true });
    } catch {
      setError('Could not delete it. Check your connection and try again.');
      setBusy(false);
    }
  };

  if (!confirming) {
    return (
      <button
        type="button"
        onClick={() => setConfirming(true)}
        className="w-full py-2 text-center text-sm font-medium text-[color:var(--color-state-slouch)]"
      >
        Delete this session
      </button>
    );
  }

  return (
    <Card className="space-y-3">
      <div>
        <p className="font-semibold">Delete this session?</p>
        <p className="mt-1 text-sm text-ink-soft">
          Use this when the measurement was off — wrong angle, someone else in frame, the phone
          moved. It leaves your history, your weekly numbers and your stage, and anything you shared
          from it is deleted too. This cannot be undone.
        </p>
      </div>
      {error ? <p className="text-sm text-[color:var(--color-state-slouch)]">{error}</p> : null}
      <div className="grid grid-cols-2 gap-2">
        <Button variant="secondary" disabled={busy} onClick={() => setConfirming(false)}>
          Keep it
        </Button>
        <Button variant="danger" disabled={busy} onClick={() => void remove()}>
          {busy ? 'Deleting…' : 'Delete'}
        </Button>
      </div>
    </Card>
  );
}
